/**
 * Why a §302 Fall could not be addressed, and must be set aside rather than
 * billed.
 *
 * Address resolution answers two questions the Kostenträgerdateien are the only
 * published source for — which Kostenträger a card-IK belongs to, and which
 * Datenannahmestelle receives its file. Neither Anlage 1 nor Anhang 3 states a
 * fallback for the cases where they do not answer, so **the fallback is to
 * refuse**: `UNB.S003 Empfänger` is a required field, and emitting to a guessed
 * recipient fails Prüfstufe 1, which rejects the *whole* Nutzdatendatei — every
 * other Fall in it included. One skipped Fall is cheap; one guess is not.
 *
 * Same shape as `DunningRunSkipReason` and `InvoiceRunSkipReason`: persisted as
 * the string literal, which is part of the domain contract and surfaces verbatim
 * in the per-row UI tooltip.
 *
 * The Abrechnungslauf (TM-9538) attaches these to its candidates pre-flight —
 * before grouping, because the Empfänger *is* the grouping key. Resolution
 * itself is a pure function and needs no run to exist.
 */
export var GkvRoutingSkipReason;
(function (GkvRoutingSkipReason) {
    /**
     * The card's Kostenträgerkennung appears in no edition of any Kassenart.
     *
     * Anhang 3 §3 frames the file as a validation aid — "um … die Zulässigkeit der
     * übermittelten IK zu prüfen" — so an unknown IK is an invalid IK. It is
     * usually a mistyped or misread Kostenträgerkennung, not a missing file.
     */
    GkvRoutingSkipReason["KOSTENTRAEGER_UNBEKANNT"] = "kostentr\u00E4ger unbekannt";
    /**
     * The card-IK resolves to a Kostenträger, but that Kostenträger publishes no
     * usable `VKG art 03` — either no `VKG` at all, or none whose Art der
     * Datenlieferung is `07`/`30` at any rung of the Abrechnungscode ladder.
     *
     * Real and undocumented: 17 IKK card-IKs terminated this way in
     * `IK05Q326.ke0`, all defunct regional entities that the file marked in no
     * way — no `Gültigkeitsdatum bis`, no `FKT 03`. IKK removed them outright in
     * `.ke1`, so the committed corpus currently has none.
     */
    GkvRoutingSkipReason["NO_RECEIVING_POINT"] = "keine annahmestelle";
    /**
     * The same card-IK is a live card-IK in more than one Kassenart, and the two
     * files route it to different Datenannahmestellen.
     *
     * The Kassenart is "which file the card-IK was found in", which presumes one
     * file — and 11 IKs are in two. All 11 are resolved by honouring
     * `Gültigkeitsdatum bis`; this reason is what happens when that is not enough,
     * and it is a refusal rather than a coin flip for the reason in the class
     * docblock.
     */
    GkvRoutingSkipReason["INSURER_TYPE_AMBIGUOUS"] = "kassenart mehrdeutig";
    /**
     * The `VKG art 01` chain does not terminate: it revisits an IK it has already
     * passed through. Corrupt published data rather than a property of the Fall,
     * but it is per-card-IK and must not take the run down with it.
     *
     * Anhang 3 states no depth bound and §5.1 is written as if the hop were always
     * single, so nothing but a cycle guard stands between a bad edition and a hang.
     */
    GkvRoutingSkipReason["ROUTING_ZYKLUS"] = "routing-zyklus";
    /**
     * A single rung of the Abrechnungscode ladder offers more than one
     * Datenannahmestelle, so "the narrowest match" does not name one row.
     *
     * Never fires over the committed corpus (0 of 258 `art 03` and 0 of 406
     * `art 09` Heilmittel rungs). It exists so that the day a publisher starts
     * fanning a rung out — presumably on a key we deliberately do not read — the
     * Fall is set aside instead of addressed by whichever row happened to be first
     * in the file.
     */
    GkvRoutingSkipReason["RECEIVING_POINT_AMBIGUOUS"] = "annahmestelle mehrdeutig";
})(GkvRoutingSkipReason || (GkvRoutingSkipReason = {}));
//# sourceMappingURL=gkv-routing-skip-reason.enum.js.map